/* eslint-disable react/no-unstable-nested-components */
/* eslint-disable react/function-component-definition */
import { getOversightClients } from '@/api/oversight';
import { ClientOverviewProps } from '@/api/oversight/types';
import { Search } from '@/components/Search';
import { SkeletonWrapper } from '@/components/SkeletonWrapper';
import { IconCloseMS } from '@/constants/icons';
import { formatDateAndTime } from '@/helpers';
import useErrorMessage from '@/hooks/useErrorMessage';
import useSnackbar from '@/hooks/useSnackbar';
import { Search as SearchIcon, WarningOutlined } from '@mui/icons-material';
import {
  ButtonBase,
  IconButton,
  InputLabel,
  MenuItem,
  Stack,
  SvgIconProps,
} from '@mui/material';
import { ArrowDropDownIcon } from '@mui/x-date-pickers';
import { ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Table } from '../../components/Table';
import {
  StyledTableCellBody,
  SubtitleCell,
} from '../../components/Table/styles';
import { RoundedGreen, TableDataHeader } from '../../styles';
import { delegatedAccessLabels, normalizeStr } from '../../utils';
import {
  AmountOFClients,
  ChipFiltersContainer,
  DeleteFilterButton,
  FilterAndSearchContainer,
  FilterContainer,
  StyledChip,
  StyledSelect,
  TableDataName,
} from './styles';

const tableHeadRow = [
  { label: 'Cliente', key: 'name' },
  { label: 'Sessões do Cliente', key: 'total_sessions' },
  { label: 'Erros nas sessões', key: 'total_client_errors' },
  { label: 'Última sessão', key: 'last_session_ended_at' },
  { label: 'Acesso delegado', key: 'delegated_session_status' },
] satisfies {
  label: string;
  key: keyof ClientOverviewProps;
}[];

type Item = {
  value: string;
  label: string;
  active?: boolean;
};

type Filter = {
  id: number;
  label: string;
  paramKey: string;
  isActive: boolean;
  items: Item[];
};

function buildParams(filters: Filter[], search: string): string {
  const params = new URLSearchParams();

  params.set('is_active', 'true');

  if (search) params.set('search', search);

  filters.forEach((filter) => {
    const activeItem = filter.items.find((item) => item.active);
    if (activeItem?.value && activeItem.value !== 'all') {
      params.set(filter.paramKey, activeItem.value);
    }
  });

  return params.toString();
}

function delegatedAccessSubtitle(row: ClientOverviewProps): string {
  if (row.delegated_session_ended_at) {
    return row.delegated_session_end_reason
      ? `${row.delegated_session_end_reason} • ${formatDateAndTime(
          row.delegated_session_ended_at
        )}`
      : formatDateAndTime(row.delegated_session_ended_at);
  }

  if (row.delegated_session_expires_at) {
    return `Expira ${formatDateAndTime(row.delegated_session_expires_at)}`;
  }

  return '';
}

export function OversightClients(): JSX.Element {
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [clientData, setClientData] = useState<ClientOverviewProps[]>([]);
  const [filtersList, setFiltersList] = useState<Filter[]>([
    {
      id: 1,
      paramKey: 'delegated_session_status',
      label: 'Acesso Delegado',
      isActive: false,
      items: [
        { value: 'all', label: 'Todos' },
        { value: 'pending_consent', label: 'Consentimento pendente' },
        { value: 'approved', label: 'Solicitação aprovada' },
        { value: 'active', label: 'Sessão delegada ativa' },
        { value: 'ended', label: 'Sessão encerrada' },
        { value: 'rejected', label: 'Solicitação negada' },
        { value: 'expired', label: 'Solicitação expirada' },
        { value: 'canceled', label: 'Solicitação cancelada' },
      ],
    },
    {
      id: 2,
      paramKey: 'has_errors',
      label: 'Erros nas sessões',
      isActive: false,
      items: [
        { value: 'all', label: 'Todos' },
        { value: 'true', label: 'Com erros' },
        { value: 'false', label: 'Sem erros' },
      ],
    },
    {
      id: 3,
      paramKey: 'last_session_status',
      label: 'Última sessão',
      isActive: false,
      items: [
        { value: 'all', label: 'Todos' },
        { value: 'online', label: 'Online' },
        { value: 'ended', label: 'Sessão encerrada' },
      ],
    },
  ]);
  const [records, setRecords] = useState(0);
  const [page, setPage] = useState(1);
  const perPage = 10;

  const isActiveFilter = filtersList.some((filter) => filter.isActive);
  const queryParams = buildParams(filtersList, normalizeStr(debouncedSearch));

  const { handleSnackbar } = useSnackbar();
  const { getErrorMessage } = useErrorMessage();

  const navigate = useNavigate();

  const handleDeleteAllFilters = (): void => {
    const newList = filtersList.map((filter) => {
      const defaultValue = filter.items[0].value;

      return {
        ...filter,
        isActive: false,
        items: filter.items.map((item) => ({
          ...item,
          active: item.value === defaultValue,
        })),
      };
    });

    setFiltersList(newList);
  };

  const handleActivateFilter = (filterId: number, itemValue: string): void => {
    const newFilterList = filtersList.map((filter) =>
      filter.id === filterId
        ? {
            ...filter,
            isActive: itemValue !== 'all',
            items: filter.items.map((item) => ({
              ...item,
              active: item.value === itemValue,
            })),
          }
        : filter
    );

    setFiltersList(newFilterList);
  };

  const handleClear = (event: React.MouseEvent, filterId: number): void => {
    event.stopPropagation();

    const newFilterList = filtersList.map((filter) => {
      if (filter.id !== filterId) {
        return filter;
      }

      const defaultValue = filter.items[0].value;

      return {
        ...filter,
        isActive: false,
        items: filter.items.map((item) => ({
          ...item,
          active: item.value === defaultValue,
        })),
      };
    });

    setFiltersList(newFilterList);
  };

  const getClients = useCallback(
    async (params: string) => {
      setIsLoading(true);
      try {
        const { data } = await getOversightClients(params, page, perPage);

        if (data) {
          setClientData(data.clients);
          setRecords(data.records);
        }
      } catch (error) {
        handleSnackbar(getErrorMessage(error), true);
      } finally {
        setIsLoading(false);
      }
    },
    [handleSnackbar, getErrorMessage, page, perPage]
  );

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, 500);

    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    getClients(queryParams);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryParams, debouncedSearch, page]);

  const handleGoToGoCustomerDetails = (id: number): void => {
    navigate(`clients/customer-details/${id}`);
  };

  const filteredData = useMemo(() => {
    return clientData
      .filter((row) => row.is_active !== false)
      .filter((row) =>
        [row.name, row.company]
          .filter(Boolean)
          .some((value) =>
            String(value).toLowerCase().includes(debouncedSearch.toLowerCase())
          )
      );
  }, [debouncedSearch, clientData]);

  return (
    <TableDataHeader>
      <FilterAndSearchContainer>
        <Search
          placeholder="Buscar por nome, empresa ou email..."
          setSearchField={setSearch}
          width="423px"
        />

        {filtersList.map((filter) => {
          const selectedValue =
            filter.items.find((item) => item.active)?.value ?? 'all';

          const SelectIcon = (props: SvgIconProps): ReactElement => {
            if (selectedValue !== 'all') {
              return (
                <IconButton onClick={(event) => handleClear(event, filter.id)}>
                  {IconCloseMS}
                </IconButton>
              );
            }

            return <ArrowDropDownIcon {...props} />;
          };

          return (
            <FilterContainer key={filter.id}>
              <InputLabel>{filter.label}</InputLabel>

              <StyledSelect
                label={filter.label}
                value={selectedValue}
                IconComponent={SelectIcon}
                displayEmpty
                onChange={(e) =>
                  handleActivateFilter(filter.id, String(e.target.value))
                }
              >
                {filter.items.map((item) => (
                  <MenuItem key={item.label} value={item.value}>
                    {item.label}
                  </MenuItem>
                ))}
              </StyledSelect>
            </FilterContainer>
          );
        })}
      </FilterAndSearchContainer>

      <ChipFiltersContainer>
        <Stack direction="row" spacing={1}>
          {filtersList.map((filter) =>
            filter.isActive ? (
              <StyledChip>
                {filter.label}:{' '}
                {filter.items.map(
                  (item) =>
                    item.active && (
                      <strong key={item.value}>{item.label}</strong>
                    )
                )}
                <DeleteFilterButton onClick={(e) => handleClear(e, filter.id)}>
                  {IconCloseMS}
                </DeleteFilterButton>
              </StyledChip>
            ) : null
          )}
        </Stack>
        {isActiveFilter && (
          <ButtonBase onClick={handleDeleteAllFilters}>
            Limpar todos os filtros {IconCloseMS}
          </ButtonBase>
        )}
      </ChipFiltersContainer>

      <TableDataName>Clientes</TableDataName>
      <AmountOFClients>
        {isLoading ? (
          <SkeletonWrapper
            isLoading={isLoading}
            variant="text"
            height={28}
            width={128}
          />
        ) : (
          `${clientData.length} clientes`
        )}
      </AmountOFClients>
      <Table
        data={filteredData}
        tableHeadData={tableHeadRow}
        getRowId={(row) => row.id}
        onRowClick={(row) => handleGoToGoCustomerDetails(row.id)}
        emptyTableIcon={<SearchIcon />}
        emptyTableDescription="Nenhum resultado encontrado"
        entityLabel="clientes"
        records={records}
        page={page}
        setPage={setPage}
        isLoading={isLoading}
        renderRow={(row) => (
          <>
            <StyledTableCellBody>
              {row.name}

              <SubtitleCell>
                <span>{row.company}</span>
              </SubtitleCell>
            </StyledTableCellBody>
            <StyledTableCellBody>{row.total_sessions}</StyledTableCellBody>
            <StyledTableCellBody>
              {row.total_client_errors === 0 ? (
                '-'
              ) : (
                <>
                  <div>
                    <WarningOutlined />
                    {row.total_client_errors}
                  </div>

                  {/* <SubtitleCell>{row.last_error}</SubtitleCell> */}
                </>
              )}
            </StyledTableCellBody>
            <StyledTableCellBody>
              {row.has_active_session ? (
                <div>
                  <RoundedGreen />
                  Online
                </div>
              ) : (
                row.last_session_ended_at
              )}
            </StyledTableCellBody>

            <StyledTableCellBody>
              {row.delegated_session_status ? (
                <>
                  <div>
                    {row.delegated_session_status === 'active' && (
                      <RoundedGreen />
                    )}
                    {delegatedAccessLabels[row.delegated_session_status] ??
                      row.delegated_session_status}
                  </div>
                  {delegatedAccessSubtitle(row) && (
                    <SubtitleCell>{delegatedAccessSubtitle(row)}</SubtitleCell>
                  )}
                </>
              ) : (
                '-'
              )}
            </StyledTableCellBody>
          </>
        )}
      />
    </TableDataHeader>
  );
}
